import { logDebug, WidgetType, getDUName } from "../js/Common";
import { useEffect, useRef } from "react";
import { bindSVGRepresentation } from "../js/SVGHelpers";
import { STATIC as InteractorObserver } from "vtk.js/Sources/Rendering/Core/InteractorObserver";
import vtkPlaneManipulator from "vtk.js/Sources/Widgets/Manipulators/PlaneManipulator";
import { SlicingMode } from "vtk.js/Sources/Rendering/Core/ImageMapper/Constants";

const isDebugging = false;

function log(msg, ...params) {
    logDebug("DicomVisualization.Crosshairs", isDebugging, msg, ...params);
}

function useCrosshairsWidget(
    parentContext,
    factory,
    activeWidget,
    slicePlaneIndex,
) {
    const parentContextRef = useRef(parentContext);
    const widgetInfoRef = useRef(null);
    const subscriptionsRef = useRef([]);

    // Setup widget once
    useEffect(() => {
        parentContextRef.current = parentContext;
        const { widgetManager, renderer, mapper, genericRenderWindow } = parentContextRef.current ?? {};
        const imageData = mapper?.getInputData();

        const isWidgetInit = !!widgetInfoRef.current;
        const areDepsReady = widgetManager && renderer && mapper && genericRenderWindow && factory && imageData;
        if (isWidgetInit || !areDepsReady) { return; }

        log("Initializing crosshairs widget...");
        const widget = widgetManager.addWidget(factory);
        widget.setPickable(false);

        const manipulator = vtkPlaneManipulator.newInstance();
        widget.setManipulator(manipulator);

        const slicingMode = mapper.getSlicingMode();
        const getVisibility = widget.getVisibility;
        const { cleanupCallbacks, updateVNode } = setupSVG(
            { widget, slicingMode, getVisibility },
            renderer,
            genericRenderWindow,
        );

        setWidgetBoundsFromImageData(widget, imageData);

        const widgetInfo = {
            widget: widget,
            currentOrigin: [0, 0, 0],
            cleanupCallbacks: cleanupCallbacks,
            updateVNode: updateVNode,
        };

        widgetInfoRef.current = widgetInfo;
    }, [parentContext, factory]);

    // Toggle widget enable state based on active widget
    useEffect(() => {
        const { renderer, mapper, widgetManager } = parentContextRef.current ?? {};
        const widget = widgetInfoRef.current?.widget;

        const areDepsReady = renderer && mapper && widgetManager && widget;
        if (!areDepsReady) { return; }

        const isActive = getDUName(activeWidget) === WidgetType.CROSSHAIR_WIDGET;

        widget.getWidgetState().setEnabled(isActive);
        widget.setPickable(isActive);

        if (isActive) {
            widgetManager.grabFocus(widget);
            subscriptionsRef.current.push(subscribeToDragEndSliceSync(widgetInfoRef.current, mapper, renderer));
            subscriptionsRef.current.push(subscribeToWidgetCoordSliceSync(widgetInfoRef.current, mapper, widget.getManipulator()));
        } else {
            clearSubscriptions(subscriptionsRef);
        }
    }, [activeWidget]);

    // Synchronize slice plane index changes
    useEffect(() => {
        const { mapper } = parentContextRef.current ?? {};
        const { widget } = widgetInfoRef.current ?? {};

        const manipulator = widget?.getManipulator();
        const mode = mapper?.getSlicingMode();
        const state = widget?.getWidgetState();
        const handle = state?.getHandle();

        const areDepsReady = mapper && widget && manipulator && mode && state && handle;
        const isModeValid = mode !== SlicingMode.NONE;
        const isDragging = state?.getDragging() === true;

        if (!areDepsReady || !isModeValid || isDragging) { return; }

        const index = getIndexFromSlicingMode(mode);
        setManipulatorSlicePlane(manipulator, index, slicePlaneIndex);

        const origin = handle.getOrigin();
        if (!origin) { return; }

        const newOrigin = [...origin];
        newOrigin[index] = slicePlaneIndex;
        handle.setOrigin(newOrigin);
    }, [slicePlaneIndex]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            log("Removing crosshairs widget...");

            const info = widgetInfoRef.current;

            clearSubscriptions(subscriptionsRef);
            info?.cleanupCallbacks?.();
            info?.widget?.delete();
        };
    }, []);
}

function subscribeToWidgetCoordSliceSync(widgetInfo, mapper, manipulator) {
    const state = widgetInfo.widget.getWidgetState();
    const handle = state.getHandle();
    const subscription = handle.onModified(() => {
        const nextOrigin = handle.getOrigin();
        const currentOrigin = widgetInfo.currentOrigin;

        if (
            !currentOrigin ||
            !nextOrigin ||
            !originChanged(currentOrigin, nextOrigin)
        ) {
            return;
        }

        const mode = mapper.getSlicingMode();
        if (mode === SlicingMode.NONE) { return; }

        const index = getIndexFromSlicingMode(mode);
        const slice = nextOrigin[index];

        setManipulatorSlicePlane(manipulator, index, slice);
        if (!state.getDragging()) { return; }

        mapper.setSliceAtFocalPoint(false);
        mapper.setSlice(slice);

        widgetInfo.currentOrigin = nextOrigin;
    });

    return subscription;
}

function subscribeToDragEndSliceSync(widgetInfo, mapper, renderer) {
    const state = widgetInfo.widget.getWidgetState();
    let wasDragging = state.getDragging();

    const subscription = state.onModified(() => {
        const isDragging = state.getDragging();

        if (isDragging) {
            wasDragging = true;
            return;
        }

        if (!wasDragging) { return; }
        wasDragging = false;

        const mode = mapper.getSlicingMode();
        if (mode === SlicingMode.NONE) { return; }

        const index = getIndexFromSlicingMode(mode);
        const crosshairSlice = mapper.getSlice();

        const camera = renderer.getActiveCamera();
        const focalPoint = [...camera.getFocalPoint()];
        const position = [...camera.getPosition()];

        const delta = crosshairSlice - focalPoint[index];
        focalPoint[index] = crosshairSlice;
        position[index] += delta;

        camera.setFocalPoint(...focalPoint);
        camera.setPosition(...position);
        mapper.setSliceAtFocalPoint(true);
        renderer.resetCameraClippingRange();
    });

    return subscription;
}

function setupSVG({ widget, slicingMode, getVisibility }, renderer, genericRenderWindow) {
    const mapState = (widgetState, { renderWindowSize }) => {
        const origin = widgetState.getHandle().getOrigin();
        if (!origin || !widgetState.getEnabled()) {
            return null;
        }

        const [dx, dy] = InteractorObserver.computeWorldToDisplay(
            renderer,
            ...origin,
        );

        const vp = renderer.getViewport();
        const [w, h] = renderWindowSize;

        const vx = vp[0] * w;
        const vy = vp[1] * h;
        const vw = (vp[2] - vp[0]) * w;
        const vh = (vp[3] - vp[1]) * h;

        const localX = dx - vx;
        const localY = dy - vy;

        const x = localX;
        const y = vh - localY;

        return { vw, vh, x, y, slicingMode, getVisibility };
    };

    const render = (data, h) => {
        if (
            !data ||
            data.vw == null ||
            data.vh == null ||
            data.x == null ||
            data.y == null ||
            isNaN(data.vw) ||
            isNaN(data.vh) ||
            isNaN(data.x) ||
            isNaN(data.y) ||
            data.slicingMode == SlicingMode.NONE ||
            !getVisibility()
        ) {
            return [];
        }

        const { vw, vh, x, y, slicingMode } = data;

        const fontSize = 24;
        const offset = 20;
        const squareSize = 20;

        const red = "#ff8080";
        const blue = "#7fb3d5";
        const yellow = "#ffeb66";
        const green = "#39FF14";

        const horizontalColours = [yellow, yellow, red];
        const verticalColours = [red, blue, blue];
        const squareColours = [blue, red, yellow];

        const topLetters = ["S", "S", "A"];
        const bottomLetters = ["I", "I", "P"];
        const leftLetters = ["P", "R", "R"];
        const rightLetters = ["A", "L", "L"];

        const index = getIndexFromSlicingMode(slicingMode);

        const horizontalColour = horizontalColours[index];
        const verticalColour = verticalColours[index];
        const squareColour = squareColours[index];

        const topLetter = topLetters[index];
        const bottomLetter = bottomLetters[index];
        const leftLetter = leftLetters[index];
        const rightLetter = rightLetters[index];

        const nodes = [
            h("g", { key: "crosshair" }, [
                // top vertical line
                h("line", {
                    attrs: {
                        x1: x,
                        y1: "0%",
                        x2: x,
                        y2: y - 8,
                        stroke: verticalColour,
                        "stroke-width": 2,
                    },
                }),
                // bottom vertical line
                h("line", {
                    attrs: {
                        x1: x,
                        y1: y + 8,
                        x2: x,
                        y2: "100%",
                        stroke: verticalColour,
                        "stroke-width": 2,
                    },
                }),
                // left horizontal line
                h("line", {
                    attrs: {
                        x1: "0%",
                        y1: y,
                        x2: x - 8,
                        y2: y,
                        stroke: horizontalColour,
                        "stroke-width": 2,
                    },
                }),
                // right horizontal line
                h("line", {
                    attrs: {
                        x1: x + 8,
                        y1: y,
                        x2: "100%",
                        y2: y,
                        stroke: horizontalColour,
                        "stroke-width": 2,
                    },
                }),
                h(
                    "text",
                    {
                        attrs: {
                            x: vw / 2,
                            y: offset,
                            "font-size": fontSize,
                            "text-anchor": "middle",
                            fill: green,
                        },
                    },
                    topLetter,
                ),
                h(
                    "text",
                    {
                        attrs: {
                            x: vw / 2,
                            y: vh,
                            "font-size": fontSize,
                            "text-anchor": "middle",
                            fill: green,
                        },
                    },
                    bottomLetter,
                ),
                h(
                    "text",
                    {
                        attrs: {
                            x: 0,
                            y: vh / 2,
                            "font-size": fontSize,
                            "text-anchor": "right",
                            fill: green,
                        },
                    },
                    leftLetter,
                ),
                h(
                    "text",
                    {
                        attrs: {
                            x: vw - offset,
                            y: vh / 2,
                            "font-size": fontSize,
                            "text-anchor": "right",
                            fill: green,
                        },
                    },
                    rightLetter,
                ),
                h(
                    'rect',
                    {
                        attrs: {
                            x: vw - squareSize,
                            y: 0,
                            width: squareSize,
                            height: squareSize,
                            fill: squareColour
                        },
                    }
                )
            ]),
        ];

        return nodes;
    };

    return bindSVGRepresentation(
        renderer,
        widget.getWidgetState(),
        genericRenderWindow,
        { mapState, render },
    );
}

function setManipulatorSlicePlane(manipulator, index, slice) {
    if (index < 0) {
        return;
    }

    const normal = [0, 0, 0];
    const origin = [0, 0, 0];

    normal[index] = 1;
    origin[index] = Math.round(slice);

    manipulator.setUserNormal(normal);
    manipulator.setUserOrigin(origin);
}

function setWidgetBoundsFromImageData(widget, imageData) {
    const state = widget.getWidgetState();
    const handle = state.getHandle();

    state.setIndexToWorld(imageData.getIndexToWorld());
    state.setWorldToIndex(imageData.getWorldToIndex());
    const [xDim, yDim, zDim] = imageData.getDimensions();
    const imageBounds = [0, xDim - 1, 0, yDim - 1, 0, zDim - 1];
    handle.setBounds(imageBounds);
}

function originChanged(a, b) {
    return a[0] !== b[0] || a[1] !== b[1] || a[2] !== b[2];
}

function getIndexFromSlicingMode(slicingMode) {
    return slicingMode > 2 ? slicingMode - 3 : slicingMode;
}

function clearSubscriptions(subscriptionsRef) {
    subscriptionsRef.current.forEach((s) => s.unsubscribe());
    subscriptionsRef.current = [];
}

export default useCrosshairsWidget;